import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useParams } from "react-router";
import Loading from "../components/Loading";
import DialogDemandeDocument from "../components/DialogDemandeDocument";
import DialogCloreAffaire from "../components/DialogCloreAffaire";
import ChoisirCreneauxRdv from "./newcalendar/ChoisirCreneauxRdv";
import MessageAffaire from "./MessageAffaire";

import TypeAffaireBadge from "../components/TypeAffaireBadge";
import { ChevronDownIcon, ChevronLeftIcon } from "@heroicons/react/16/solid";

// immobilier
import TabsAffaireBien from "./immobilier/TabsAffaireBien";
import TabsAffaireInformationsImmo from "./immobilier/TabsAffaireInformations";
import TabsAffaireRdv from "./immobilier/TabsAffaireRdv";

// pma
import TabsAffaireRdvPma from "./pma/TabsAffaireRdvPma";
// mariage
import TabsAffaireRdvMariage from "./mariage/TabsAffaireRdvMariage";
// pacs
import TabsAffaireRdvPacs from "./pacs/TabsAffaireRdvPacs";
// certification
import TabsAffaireRdvCertification from "./certification/TabsAffaireRdvCertification";
// bail
import TabsAffaireRdvBail from "./bail/TabsAffaireRdvBail";
// donation
import TabsAffaireRdvDonation from "./donation/TabsAffaireRdvDonation";
// succession
import TabsAffaireRdvSuccession from "./succession/TabsAffaireRdvSuccession";
// divorce
import TabsAffaireRdvDivorce from "./divorce/TabsAffaireRdvDivorce";

// autre
import TabsAffaireInformationsAutre from "./autre/TabsAffaireInformations";
import TabsAffaireRdvAutre from "./autre/TabsAffaireRdv";

// commun
import TabsAffaireDocumentsGeneric from "./TabsAffaireDocumentsGeneric";
import TabsAffaireInformations from "./TabsAffaireInformationsGeneric";
import TabsAffaireHonoraires from "./TabsAffaireHonoraires";

import { API_BASE } from "../config";

const fetchAirtableData = async (base, id) => {
  const res = await fetch(`${API_BASE}/api/affaire-notaire/${base}/${id}`);
  if (!res.ok) throw new Error(`Erreur serveur: ${res.status}`);
  return res.json();
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}



function Affaire(args) {
  const { type, id } = useParams();

  const [affaire, setAffaire] = useState(null);
  const [bien, setBien] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingPma, setLoadingPma] = useState(false);
  const [loadingMariage, setLoadingMariage] = useState(false);
  const [loadingPacs, setLoadingPacs] = useState(false);
  const [loadingCertification, setLoadingCertification] = useState(false);
  const [loadingBail, setLoadingBail] = useState(false);
  const [loadingDonation, setLoadingDonation] = useState(false);
  const [loadingDivorce, setLoadingDivorce] = useState(false);
  const [loadingSuccession, setLoadingSuccession] = useState(false);
  const [loadingAucuneDemarche, setLoadingAucuneDemarche] = useState(false);
  const [loadingAutre, setLoadingAutre] = useState(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDialogOpenClore, setIsDialogOpenClore] = useState(false);

  const initials = useMemo(() => {
    const raw = affaire?.nom || affaire?.nom_from_user || "";
    const name = Array.isArray(raw) ? (raw[0] || "") : String(raw);
    return name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?";
  }, [affaire]);

  const avatarColorByType = {
    immo: "bg-cyan-100 text-cyan-700",
    divorce: "bg-rose-100 text-rose-700",
    mariage: "bg-violet-100 text-violet-700",
    pma: "bg-blue-100 text-blue-700",
    pacs: "bg-gray-100 text-gray-700",
    donation: "bg-green-100 text-green-700",
    bail: "bg-emerald-100 text-emerald-700",
    succession: "bg-amber-100 text-amber-700",
    testament: "bg-gray-100 text-gray-700",
    certification: "bg-teal-100 text-teal-700",
    adoption: "bg-fuchsia-100 text-fuchsia-700",
    "statut-societe": "bg-stone-100 text-stone-700",
    procuration: "bg-sky-100 text-sky-700",
  };
  const avatarColor = avatarColorByType[type] || "bg-indigo-100 text-indigo-700";

  const participants = useMemo(() => {
  if (!affaire) return [];

  const arr = [];
  const pushIf = (email, prenom, nom, role, telephone) => {
    const e = String(email || "").trim();
    if (!e) return;
    arr.push({
      email: e,
      prenom: Array.isArray(prenom) ? prenom?.[0] : prenom,
      nom: Array.isArray(nom) ? nom?.[0] : nom,
      role,
      telephone: Array.isArray(telephone) ? telephone?.[0] : telephone,
    });
  };

  // 🔹 NOTAIRE (souvent présent dans transaction immo)
  pushIf(
    affaire.email_from_notaire?.[0],
    affaire.prenom_from_notaire?.[0],
    affaire.nom_from_notaire?.[0],
    "Notaire",
    affaire.telephone_from_notaire?.[0]
  );

  // 🔹 IMMOBILIER : acheteur / vendeur / partenaire
  if (type === "immo") {
    pushIf(
      affaire.email_from_acheteur?.[0],
      affaire.prenom_from_acheteur?.[0],
      affaire.nom_from_acheteur?.[0],
      "Acheteur",
      affaire.telephone_from_acheteur?.[0]
    );

    pushIf(
      affaire.email_from_vendeur?.[0],
      affaire.prenom_from_vendeur?.[0],
      affaire.nom_from_vendeur?.[0],
      "Vendeur",
      affaire.telephone_from_vendeur?.[0]
    );

    pushIf(
      affaire.email_from_partenaire?.[0],
      affaire.prenom_from_partenaire?.[0],
      affaire.nom_from_partenaire?.[0],
      "Partenaire",
      affaire.telephone_from_partenaire?.[0]
    );
  } else {
    // 🔹 AUTRES TYPES : user + partenaire (pas acheteur/vendeur)
    pushIf(
      affaire.email_from_user?.[0],
      affaire.prenom_from_user?.[0],
      affaire.nom_from_user?.[0],
      "Client",
      affaire.telephone_from_user?.[0]
    );

    pushIf(
      affaire.email_from_partenaire?.[0],
      affaire.prenom_from_partenaire?.[0],
      affaire.nom_from_partenaire?.[0],
      "Partenaire",
      affaire.telephone_from_partenaire?.[0]
    );
  }

  // dedupe by email
  const m = new Map();
  for (const p of arr) m.set(String(p.email).toLowerCase().trim(), p);
  return Array.from(m.values());
}, [affaire, type]);

  const isAutreType =
    type === "succession" ||
    type === "testament" ||
    type === "adoption" ||
    type === "statut-societe" ||
    type === "procuration";

  // ✅ mapping : les routes "succession/testament/..." lisent dans la table "autre_demarche"
  const baseByType = (t) => {
    if (t === "immo") return "transaction";
    if (t === "aucune_demarche") return "aucune_demarche";
    if (
      t === "succession" ||
      t === "testament" ||
      t === "adoption" ||
      t === "statut-societe" ||
      t === "procuration"
    ) {
      return "autre_demarche";
    }
    return t;
  };

  // Charge affaire selon type
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      try {
        const base = baseByType(type);
        const res = await fetchAirtableData(base, id);

        const fields = res?.fields || {};
        const normalized =
          base === "autre_demarche" ? { ...fields, type: type } : fields;

        setAffaire({ id: res.id, ...normalized });

        // loaders
        if (type === "immo") setLoading(true);
        else if (type === "pma") setLoadingPma(true);
        else if (type === "mariage") setLoadingMariage(true);
        else if (type === "pacs") setLoadingPacs(true);
        else if (type === "certification") setLoadingCertification(true);
        else if (type === "bail") setLoadingBail(true);
        else if (type === "donation") setLoadingDonation(true);
        else if (type === "divorce") setLoadingDivorce(true);
        else if (type === "succession") setLoadingSuccession(true);
        else if (isAutreType) setLoadingAutre(true);
        else if (type === "aucune_demarche") setLoadingAucuneDemarche(true);
      } catch (e) {
        console.error(e);
      }
    };

    // reset loaders quand on change d'affaire
    setLoading(false);
    setLoadingPma(false);
    setLoadingMariage(false);
    setLoadingPacs(false);
    setLoadingCertification(false);
    setLoadingBail(false);
    setLoadingDonation(false);
    setLoadingDivorce(false);
    setLoadingAucuneDemarche(false);
    setLoadingAutre(false);
    setAffaire(null);

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, type]);



  // ✅ Tabs dépendantes de isAutreType
  const tabs = useMemo(
    () => [
      { id: "info", label: "Informations" },
      { id: "document", label: "Documents à collecter" }
    ],
    [isAutreType]
  );
  {/** on cache les tabs honoraires et rendez-vous pour l'instant
      ...(!isAutreType ? [{ id: "honoraires", label: "Honoraires" }] : []),
      { id: "rdv", label: "Rendez-vous" }, */}

  const [activeTab, setActiveTab] = useState("info");

  useEffect(() => {
    setActiveTab("info");
  }, [type]);

  useEffect(() => {
    const exists = tabs.some((t) => t.id === activeTab);
    if (!exists) setActiveTab("info");
  }, [tabs, activeTab]);

  const handleSelectChange = (e) => {
    const selectedId = tabs.find((tab) => tab.label === e.target.value)?.id;
    if (selectedId) setActiveTab(selectedId);
  };

  const tabContent = {
    info: (() => {
      if (type === "immo")
        return (
          <TabsAffaireInformationsImmo
            affaire={affaire}
            onOpenDocuments={setActiveTab}
            onOpenMessagerie={setActiveTab}
          />
        );
      if (type === "pma")
        return (
          <TabsAffaireInformations
            type="pma"
            affaire={affaire}
            onOpenDocuments={setActiveTab}
            onOpenMessagerie={setActiveTab}
          />
        );
      if (type === "mariage")
        return (
          <TabsAffaireInformations
            type="mariage"
            affaire={affaire}
            onOpenDocuments={setActiveTab}
            onOpenMessagerie={setActiveTab}
          />
        );
      if (type === "pacs")
        return (
          <TabsAffaireInformations
            type="pacs"
            affaire={affaire}
            onOpenDocuments={setActiveTab}
            onOpenMessagerie={setActiveTab}
          />
        );
      if (type === "certification")
        return (
          <TabsAffaireInformations
            type="certification"
            affaire={affaire}
            onOpenDocuments={setActiveTab}
            onOpenMessagerie={setActiveTab}
          />
        );
      if (type === "bail")
        return (
          <TabsAffaireInformations
            type="bail"
            affaire={affaire}
            onOpenDocuments={setActiveTab}
            onOpenMessagerie={setActiveTab}
          />
        );
      if (type === "donation")
        return (
          <TabsAffaireInformations
            type="donation"
            affaire={affaire}
            onOpenDocuments={setActiveTab}
            onOpenMessagerie={setActiveTab}
          />
        );
      if (type === "divorce")
        return (
          <TabsAffaireInformations
            type="divorce"
            affaire={affaire}
            onOpenDocuments={setActiveTab}
            onOpenMessagerie={setActiveTab}
          />
        );
        if (type === "succession")
        return (
          <TabsAffaireInformations
            type="succession"
            affaire={affaire}
            onOpenDocuments={setActiveTab}
            onOpenMessagerie={setActiveTab}
          />
        );
      if (isAutreType)
        return (
          <TabsAffaireInformationsAutre
            type="autre"
            affaire={affaire}
            onOpenDocuments={setActiveTab}
            onOpenMessagerie={setActiveTab}
          />
        );
      if (type === "aucune_demarche")
        return (
          <TabsAffaireInformations
            type="aucune_demarche"
            affaire={affaire}
            onOpenDocuments={setActiveTab}
            onOpenMessagerie={setActiveTab}
          />
        );
      return <p>Pas d'information disponible.</p>;
    })(),

    document: <TabsAffaireDocumentsGeneric affaire={affaire} type={type} />,

    rdv: (() => {
      if (type === "immo") return <TabsAffaireRdv affaire={affaire} type={type} user={args.userInfo} />;
      if (type === "mariage") return <TabsAffaireRdvMariage affaire={affaire} type={type} user={args.userInfo} />;
      if (type === "pacs") return <TabsAffaireRdvPacs affaire={affaire} type={type} user={args.userInfo} />;
      if (type === "pma") return <TabsAffaireRdvPma affaire={affaire} type={type} user={args.userInfo} />;
      if (type === "certification") return <TabsAffaireRdvCertification affaire={affaire} type={type} user={args.userInfo} />;
      if (type === "bail") return <TabsAffaireRdvBail affaire={affaire} type={type} user={args.userInfo} />;
      if (type === "donation") return <TabsAffaireRdvDonation affaire={affaire} type={type} user={args.userInfo} />;
      if (type === "divorce") return <TabsAffaireRdvDivorce affaire={affaire} type={type} user={args.userInfo} />;
      if (type === "succession") return <TabsAffaireRdvSuccession affaire={affaire} type={type} user={args.userInfo} />;
      if (isAutreType) return <TabsAffaireRdvAutre affaire={affaire} type={type} user={args.userInfo} />;
      return <p>Pas d'information disponible.</p>;
    })(),

    honoraires: !isAutreType ? (
      <TabsAffaireHonoraires affaire={affaire} type={type} user={args.userInfo} />
    ) : null,
  };

  if (type === "immo") tabContent.bien = <TabsAffaireBien {...affaire} bien={bien} />;

  const ready =
    (type === "immo" && loading && affaire) ||
    (type === "pma" && loadingPma && affaire) ||
    (type === "mariage" && loadingMariage && affaire) ||
    (type === "pacs" && loadingPacs && affaire) ||
    (type === "certification" && loadingCertification && affaire) ||
    (type === "bail" && loadingBail && affaire) ||
    (type === "donation" && loadingDonation && affaire) ||
    (type === "divorce" && loadingDivorce && affaire) ||
    (type === "succession" && loadingSuccession && affaire) ||
    (isAutreType && loadingAutre && affaire) ||
    (type === "aucune_demarche" && loadingAucuneDemarche && affaire);

  if (!ready) return <Loading />;

  return (
    <>
      <main>
        {/* header */}
        <header className="bg-white border-b border-gray-200">
          <div className="max-w-[90%] mx-auto pt-5 pb-0">

            {/* Breadcrumb */}
            <Link
              to="/app/affaires"
              className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-4 no-underline"
            >
              <ChevronLeftIcon className="size-4" />
              Retour aux affaires
            </Link>

            {/* Identité + actions */}
            <div className="mx-auto flex max-w-2xl items-center justify-between gap-x-8 lg:mx-0 lg:max-w-none pb-5">

              {/* Gauche : avatar initiales + type badge + nom */}
              <div className="flex items-center gap-x-6">
                <span className={`inline-flex size-12 shrink-0 items-center justify-center rounded-full text-base font-bold ${avatarColor}`}>
                  {initials}
                </span>
                <h1>
                  <div className="text-sm/6 text-gray-500">
                    <TypeAffaireBadge type={type} />
                  </div>
                  <div className="mt-1 text-base font-semibold text-gray-900">
                  Affaire <b>{affaire?.nom || affaire?.nom_from_user} {" "}{affaire?.prenom || affaire?.prenom_from_user}</b>
                  </div>
                </h1>
              </div>

              {/* Droite : actions originales */}
              <div className="flex items-center gap-x-4 sm:gap-x-6">
                <button
                  type="button"
                  className="hidden text-sm/6 text-gray-900 sm:block bg-red-200 rounded-full px-3 py-2 text-sm font-semibold"
                  onClick={() => setIsDialogOpenClore(true)}
                >
                  Clore l'affaire
                </button>

                <button
                  className="btn-main"
                  onClick={() => setIsDialogOpen(true)}
                >
                  Demander un document
                </button>

                <ChoisirCreneauxRdv
                  where="header"
                  id_transaction={affaire?.id_transaction}
                  type={type}
                  notaireAirtableId={affaire?.notaire?.[0] || affaire?.notaire_link?.[0]}
                  prenomNotaire={affaire?.prenom_from_notaire}
                  nomNotaire={affaire?.nom_from_notaire}
                  emailUser={affaire?.email_from_user}
                />
              </div>
            </div>
          </div>

          {/* TABS */}
          <div className="max-w-[90%] mx-auto">
            {/* Mobile (select) */}
            <div className="grid grid-cols-1 sm:hidden relative">
              <select
                value={tabs.find((t) => t.id === activeTab)?.label}
                onChange={handleSelectChange}
                aria-label="Select a tab"
                className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pr-8 pl-3 text-base text-gray-900 outline outline-1 outline-gray-300 focus:outline-2 focus:outline-indigo-600"
              >
                {tabs.map((tab) => (
                  <option key={tab.id}>{tab.label}</option>
                ))}
              </select>

              <ChevronDownIcon
                aria-hidden="true"
                className="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end fill-gray-500"
              />
            </div>

            {/* Desktop (tabs) */}
            <div className="hidden sm:block">
              <div className="border-b border-gray-200">
                <nav aria-label="Tabs" className="-mb-px flex space-x-8">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setActiveTab(tab.id)}
                      aria-current={activeTab === tab.id ? "page" : undefined}
                      className={classNames(
                        activeTab === tab.id
                          ? "border-indigo-500 text-indigo-600 font-bold"
                          : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                        "border-b-2 px-1 py-4 text-sm font-bold whitespace-nowrap"
                      )}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="bg-slate-100 pb-10 ">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mx-auto max-w-[90%]">
  {/* GAUCHE : contenu affaire */}
  <div className="lg:col-span-7 xl:col-span-8 pt-6">
    {tabContent[activeTab]}
  </div>

  {/* DROITE : messagerie FIXE */}
  <div className="lg:col-span-5 xl:col-span-4 pt-6">
    <div className="sticky top-24 h-[calc(100vh-120px)]">
      <div className="bg-white rounded-2xl shadow overflow-hidden h-full flex flex-col">
        {/* Header stylé */}
        <div className="p-4 border-b border-gray-100">
          <p className="text-sm font-semibold text-gray-900 mb-1">Messagerie</p>
        </div>

        {/* MessagingShell */}
        <div className="flex-1 min-h-0">
          <MessageAffaire
            affaireId={id}
            participants={participants}
            emptyTitle="Aucun message pour l’instant"
            emptySubtitle="Envoyez un premier message à un participant."
          />
        </div>
      </div>
    </div>
  </div>
</div>

          <DialogDemandeDocument
            id_affaire={id}
            type={type}
            isOpen={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
          />
          <DialogCloreAffaire
            id_affaire={id}
            type={type}
            isOpen={isDialogOpenClore}
            onClose={() => setIsDialogOpenClore(false)}
          />
        </div>
      </main>
    </>
  );
}

export default Affaire;